import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Handshake, Sparkle, Target, Storefront, ShieldCheck, Calendar, BookOpen, Cake, Lock, Plant, Coffee } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const CANDIDATE_ICONS = {
  orderjar: Cake,
  quietroom: Lock,
  "chapter-one": BookOpen,
  "popup-prep": Coffee,
  "round-runner": Plant,
};

const STEPS = [
  { n: "01", title: "Bring us a real problem.", body: "Something software hasn't solved well for your trade — because the audience is too niche for venture-funded SaaS to bother with." },
  { n: "02", title: "We co-design and co-build.", body: "Two-week design sprint. You shape every feature. A working app in 4–6 weeks. You use it daily — that's the test." },
  { n: "03", title: "Free for 6 months. Half-price forever.", body: "While we polish it, you use it free. Then it goes on the App Store at the public price — and you keep using it forever at 50% off, no questions asked." },
];

export default function CoInnovation() {
  const { settings, loading: settingsLoading } = useSiteSettings();
  const [demos, setDemos] = useState([]);
  const [slots, setSlots] = useState({ quarter_label: "H1 2026", total_slots: 3, taken_slots: 0, slots_left: 3 });
  useEffect(() => {
    if (!settings.co_innovation_enabled) return;
    api.get("/demos").then(({ data }) => {
      setDemos((data || []).filter((d) => d.co_innovation));
    }).catch(() => {});
    api.get("/co-innovation/slots").then(({ data }) => {
      if (data) setSlots(data);
    }).catch(() => {});
  }, [settings.co_innovation_enabled]);

  // When the feature is disabled, the page does not exist for the public — redirect silently to home.
  // We wait for settings to load first so we don't bounce users on first paint.
  if (!settingsLoading && !settings.co_innovation_enabled) {
    return <Navigate to="/" replace />;
  }

  const { quarter_label, total_slots, taken_slots, slots_left } = slots;
  const isFull = slots_left === 0;
  const nextLabel = (quarter_label || "").toLowerCase().includes("h1") ? "H2" : "H1";

  return (
    <div className="bg-[#FBF8F2] text-zinc-950" data-testid="co-innovation-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200">
        <div className="absolute inset-0 grid-dots opacity-[0.05]" />
        <div className="app-container py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end relative">
          <div className="lg:col-span-7">
            <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-8" data-testid="co-innovation-home">
              <ArrowLeft size={14} /> Home
            </Link>
            <p className="overline mb-4 text-[hsl(var(--appistry-signal))]">The co-innovation programme</p>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Build it with us.<br/>
              <span className="text-[hsl(var(--appistry-signal))]">Get it before the world does.</span>
            </h1>
            <p className="text-xl text-zinc-700 leading-relaxed max-w-2xl mb-8">
              Three founding-partner slots open every six months. Bring a problem off-the-shelf software has ignored — we co-build the answer, you keep using it forever at half price, and we sell it on the App Store.
            </p>
            <div className="flex flex-wrap gap-3">
              {isFull ? (
                <Link to="/consultation?source=co-innovation-waitlist" className="btn-signal" data-testid="co-innovation-cta-hero">
                  <Handshake size={18} weight="fill" /> Join the {nextLabel} waitlist <ArrowRight size={14} weight="bold" />
                </Link>
              ) : (
                <Link to="/consultation?source=co-innovation" className="btn-signal" data-testid="co-innovation-cta-hero">
                  <Handshake size={18} weight="fill" /> Apply for a founding slot <ArrowRight size={14} weight="bold" />
                </Link>
              )}
              <a href="#how" className="btn-ghost-ink">How it works ↓</a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-zinc-950 text-white p-8 relative" data-testid="co-innovation-counter">
              <div className="absolute top-0 right-0 bg-[hsl(var(--appistry-signal))] text-zinc-950 px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold">{quarter_label}</div>
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-3">
                {isFull ? "This period is full" : `Founding slots remaining · ${quarter_label}`}
              </p>
              <div className="flex items-end gap-4">
                <div className={`text-[120px] font-black leading-none tracking-tighter ${isFull ? "text-zinc-600" : ""}`} data-testid="co-innovation-slots-number">{slots_left}</div>
                <div className="text-base font-mono uppercase tracking-widest text-zinc-500 pb-6">of {total_slots}</div>
              </div>
              {/* Pip row showing taken vs open */}
              <div className="flex gap-2 mt-2 mb-6">
                {Array.from({ length: total_slots }).map((_, i) => (
                  <div key={i} className="flex-1 h-2" style={{ backgroundColor: i < taken_slots ? "#3F3F46" : "hsl(var(--appistry-signal))" }} />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4 pt-6 border-t border-zinc-800">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Free for</p>
                  <p className="text-2xl font-bold">6 months</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Then forever at</p>
                  <p className="text-2xl font-bold text-[hsl(var(--appistry-signal))]">−50%</p>
                </div>
              </div>
              {isFull ? (
                <p className="text-sm text-zinc-400 mt-6"><b className="text-[hsl(var(--appistry-signal))]">All slots taken for {quarter_label}.</b> Join the waitlist — new slots open every six months.</p>
              ) : taken_slots > 0 ? (
                <p className="text-sm text-zinc-400 mt-6"><b className="text-white">{taken_slots} signed · {slots_left} left.</b> Capped to keep the programme curated and the founding partners actually heard.</p>
              ) : (
                <p className="text-sm text-zinc-400 mt-6">Capped to keep the programme curated — and the founding partners actually heard.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-b border-zinc-200 bg-white">
        <div className="app-container py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <p className="overline mb-3">How it works</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>Three steps. No surprises.</h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg text-zinc-700 leading-relaxed">We've built dozens of apps for clients. Co-innovation is the same craft, but we share the upside both ways: you de-risk the build, we get a polished concept ready for the App Store, and you become the named founding partner on the case study.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="border border-zinc-200 bg-[#FBF8F2] p-7 relative" data-testid={`co-innovation-step-${s.n}`}>
                <div className="text-6xl font-black tracking-tighter text-[hsl(var(--appistry-signal))] mb-4">{s.n}</div>
                <h3 className="text-xl font-bold tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.title}</h3>
                <p className="text-sm text-zinc-700 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CANDIDATES */}
      <section className="border-b border-zinc-200">
        <div className="app-container py-20 md:py-28">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="overline mb-3">Open for founding partners</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>What we're co-building right now.</h2>
            </div>
            <Link to="/demos" className="text-sm font-mono uppercase tracking-widest text-zinc-600 hover:text-zinc-900">All demos →</Link>
          </div>
          {demos.length === 0 ? (
            <p className="text-sm font-mono text-zinc-500">Loading candidates…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="co-innovation-candidates">
              {demos.map((d) => {
                const Icon = CANDIDATE_ICONS[d.slug] || Sparkle;
                return (
                  <Link key={d.slug} to={`/demos/${d.slug}`} className="group border border-zinc-200 bg-white p-7 hover:border-zinc-950 transition-colors flex gap-5" data-testid={`co-innovation-candidate-${d.slug}`}>
                    <div className="h-14 w-14 bg-zinc-950 flex-shrink-0 flex items-center justify-center group-hover:bg-[hsl(var(--appistry-signal))] transition-colors">
                      <Icon size={24} weight="fill" className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{d.category}</p>
                      <h3 className="text-2xl font-black tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{d.title}</h3>
                      <p className="text-sm text-zinc-700 leading-relaxed mb-3 line-clamp-3">{d.short_description}</p>
                      <p className="text-[11px] font-mono uppercase tracking-widest text-[hsl(var(--appistry-signal))] font-bold">{d.client}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* DEAL TERMS */}
      <section className="bg-zinc-950 text-white border-b border-zinc-800">
        <div className="app-container py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="overline mb-3 text-[hsl(var(--appistry-signal))]">The deal</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Plain English. Fair both ways.</h2>
              <p className="text-zinc-400 leading-relaxed">No NDAs you can't read. No equity grabs. Just a clear partnership.</p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <DealCard icon={Sparkle} title="You shape it." body="Weekly working sessions. Your real day-to-day drives every feature decision." />
              <DealCard icon={Calendar} title="6 months free." body="Full access from week one. We polish in the open while you use it daily." />
              <DealCard icon={ShieldCheck} title="50% off forever." body="When it lists publicly, you keep your subscription at half the App Store price — for the lifetime of the product." />
              <DealCard icon={Target} title="Named founding partner." body="Your business gets the case-study credit. Mention or anonymity — your choice." />
              <DealCard icon={Storefront} title="We list, we sell, we maintain." body="App Store distribution, billing, support and updates are on us. You just use it." />
              <DealCard icon={Handshake} title="No equity, no IP transfer." body="We keep the IP and the App Store revenue. You keep your half-price tool. Clean lines." />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FBF8F2]">
        <div className="app-container py-20 md:py-28 text-center max-w-3xl mx-auto">
          <p className="overline mb-3 text-[hsl(var(--appistry-signal))]">{isFull ? `${quarter_label} · waitlist open` : `Apply for ${quarter_label}`}</p>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {isFull ? "All slots taken. Join the next round." : `${slots_left} slot${slots_left > 1 ? "s" : ""}. One form. 30 minutes.`}
          </h2>
          <p className="text-lg text-zinc-700 leading-relaxed mb-8">Tell us your trade and the daily friction. If it's a fit, we'll book the design sprint within a fortnight.</p>
          <Link to={isFull ? "/consultation?source=co-innovation-waitlist" : "/consultation?source=co-innovation"} className="btn-signal text-base px-7 py-4" data-testid="co-innovation-cta-bottom">
            <Handshake size={18} weight="fill" /> {isFull ? `Join the ${nextLabel} waitlist` : "Apply for a founding slot"} <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function DealCard({ icon: Icon, title, body }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900 p-6 relative">
      <Icon size={22} weight="fill" className="text-[hsl(var(--appistry-signal))] mb-3" />
      <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}
